import { useState, useEffect, useRef, useCallback } from 'react';
import { thesisApi } from '@/api/thesisApi';
import { Button } from '@/components/ui/button';
import { Save, Sparkles, Eye, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import ClaimHighlightOverlay from '@/components/ClaimHighlightOverlay';
import ClaimConfidenceCard from '@/components/ClaimConfidenceCard';

/**
 * Live editor mode for a chapter — a plain textarea with auto-save, an
 * on-demand "AI suggest" button and optional inline claim confidence.
 *
 *   - Auto-save 1.8 s after the user stops typing.
 *   - Claim confidence (off by default, toggle in the toolbar): 3 s after you
 *     stop typing the paragraph under the caret is graded sentence by sentence
 *     against the evidence library; wavy underlines mark the verdict and
 *     clicking a marked sentence opens the evidence + fix card.
 *   - Non-destructive `onChapterSaved(chapterId, newMarkdown)`.
 */
const CC_KEY = 'inkphd-claim-confidence';
const MIN_PARA = 80;

const paragraphAt = (text, caret) => {
  const before = text.lastIndexOf('\n\n', Math.max(0, caret - 1));
  const start = before === -1 ? 0 : before + 2;
  const after = text.indexOf('\n\n', caret);
  return { start, end: after === -1 ? text.length : after };
};

const reanchor = (claims, text) => claims.flatMap((c) => {
  if (text.slice(c.start, c.end) === c.text) return [c];
  const near = text.indexOf(c.text, Math.max(0, c.start - 500));
  const at = near === -1 ? text.indexOf(c.text) : near;
  return at === -1 ? [] : [{ ...c, start: at, end: at + c.text.length }];
});

export default function ChapterLiveEditor({ userId, projectId, chapter, language = 'en', onChapterSaved, onClose }) {
  const [value, setValue] = useState(chapter?.markdown || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [ccOn, setCcOn] = useState(() => localStorage.getItem(CC_KEY) === '1');
  const [claims, setClaims] = useState([]);
  const [checking, setChecking] = useState(false);
  const [activeClaim, setActiveClaim] = useState(null);

  const taRef = useRef(null);
  const overlayRef = useRef(null);
  const saveTimer = useRef(null);
  const ccTimer = useRef(null);
  const ccDirtyRef = useRef(false);
  const lastParaRef = useRef('');
  const cursorPosRef = useRef((chapter?.markdown || '').length);

  // ---- AUTO-SAVE ----
  useEffect(() => {
    if (!dirty) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await thesisApi.updateChapter(userId, chapter.id, { markdown: value });
        setDirty(false);
        onChapterSaved?.(chapter.id, value);
      } catch (e) {
        toast.error('Auto-save failed: ' + (e.response?.data?.detail || e.message));
      } finally {
        setSaving(false);
      }
    }, 1800);
    return () => clearTimeout(saveTimer.current);
  }, [value, dirty, userId, chapter?.id, onChapterSaved]);

  // ---- CLAIM CONFIDENCE ----
  const mergeClaims = useCallback((incoming, from, to) => {
    setClaims((prev) => [...prev.filter((c) => c.end <= from || c.start >= to), ...incoming]
      .sort((a, b) => a.start - b.start));
  }, []);

  const runCheck = useCallback(async (text, offset, from, to, quiet) => {
    if (!text || text.trim().length < MIN_PARA) return;
    setChecking(true);
    try {
      const res = await thesisApi.claimConfidence(userId, {
        chapter_id: chapter.id, text, offset, language,
        max_sentences: offset === 0 && from === 0 ? 30 : 10,
      });
      mergeClaims(res.sentences || [], from, to);
      if (!quiet) {
        const s = res.stats || {};
        toast.success(`${res.checked} claims checked · ${s.unsupported || 0} unsupported, `
          + `${s.partial || 0} weak, ${s.mismatches || 0} citation mismatches`);
      }
    } catch (e) {
      if (!quiet) toast.error('Claim check failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setChecking(false);
    }
  }, [userId, chapter?.id, language, mergeClaims]);

  useEffect(() => {
    if (!ccOn || !ccDirtyRef.current) return undefined;
    if (ccTimer.current) clearTimeout(ccTimer.current);
    ccTimer.current = setTimeout(() => {
      const { start, end } = paragraphAt(value, cursorPosRef.current);
      const para = value.slice(start, end);
      if (para.trim().length < MIN_PARA || para === lastParaRef.current) return;
      lastParaRef.current = para;
      runCheck(para, start, start, end, true);
    }, 3000);
    return () => clearTimeout(ccTimer.current);
  }, [value, ccOn, runCheck]);

  useEffect(() => {
    setClaims((prev) => (prev.length ? reanchor(prev, value) : prev));
  }, [value]);

  const toggleCc = () => {
    const next = !ccOn;
    setCcOn(next);
    localStorage.setItem(CC_KEY, next ? '1' : '0');
    if (!next) { setClaims([]); setActiveClaim(null); }
  };

  const syncScroll = useCallback(() => {
    if (overlayRef.current && taRef.current) overlayRef.current.scrollTop = taRef.current.scrollTop;
  }, []);

  const handleChange = useCallback((e) => {
    cursorPosRef.current = e.target.selectionStart;
    ccDirtyRef.current = true;
    setValue(e.target.value);
    setDirty(true);
    syncScroll();
  }, [syncScroll]);

  const handleSelect = useCallback((e) => {
    cursorPosRef.current = e.target.selectionStart;
    if (!ccOn) return;
    const caret = e.target.selectionStart;
    setActiveClaim(claims.find((c) => caret >= c.start && caret <= c.end) || null);
  }, [ccOn, claims]);

  const insertCitation = useCallback((token) => {
    if (!activeClaim || !token) return;
    const at = /[.!?]$/.test(value.slice(activeClaim.end - 1, activeClaim.end))
      ? activeClaim.end - 1 : activeClaim.end;
    const next = `${value.slice(0, at)} ${token}${value.slice(at)}`;
    ccDirtyRef.current = true;
    setValue(next);
    setDirty(true);
    setActiveClaim(null);
    toast.success(`Citation ${token} inserted`);
  }, [activeClaim, value]);

  // ---- MANUAL AI SUGGEST (paragraph-completion) ----
  const handleSuggest = useCallback(async () => {
    const cp = cursorPosRef.current;
    const preceding = value.slice(0, cp);
    if (preceding.length < 60) {
      toast.error('Write a bit more first so the AI has context.');
      return;
    }
    setSuggesting(true);
    try {
      const data = await thesisApi.continueWriting(userId, projectId, preceding, language, 2);
      const cont = (data?.continuation || '').trim();
      if (!cont) {
        toast.warning('AI returned an empty continuation. Try again.');
        return;
      }
      const lastChar = preceding.slice(-1);
      const needsSpace = lastChar && !/\s/.test(lastChar);
      const inserted = (needsSpace ? ' ' : '') + cont;
      const newValue = value.slice(0, cp) + inserted + value.slice(cp);
      const newCursor = cp + inserted.length;
      ccDirtyRef.current = true;
      setValue(newValue);
      setDirty(true);
      cursorPosRef.current = newCursor;
      setTimeout(() => {
        const ta = taRef.current;
        if (ta) {
          ta.focus();
          ta.setSelectionRange(newCursor, newCursor);
        }
      }, 0);
      toast.success(`Added ${cont.split(/\s+/).length} words`, { duration: 1500 });
    } catch (e) {
      toast.error('Suggestion failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSuggesting(false);
    }
  }, [value, userId, projectId, language]);

  const handleManualSave = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaving(true);
    try {
      await thesisApi.updateChapter(userId, chapter.id, { markdown: value });
      setDirty(false);
      onChapterSaved?.(chapter.id, value);
      toast.success('Saved');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const wordCount = (value.match(/\b\w+\b/g) || []).length;
  const charCount = value.length;
  const flagged = claims.filter((c) => c.verdict !== 'supported').length;
  const boxCls = 'w-full font-mono text-[14px] leading-[1.65] px-4 py-3';

  return (
    <div className="space-y-3" data-testid="chapter-live-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span data-testid="editor-word-count">{wordCount} words</span>
          <span className="opacity-50">·</span>
          <span data-testid="editor-char-count">{charCount} chars</span>
          {saving && (
            <span className="flex items-center gap-1 text-muted-foreground" data-testid="editor-saving">
              <Loader2 className="w-3 h-3 animate-spin" /> saving...
            </span>
          )}
          {!saving && !dirty && (
            <span className="text-emerald-600 dark:text-emerald-400 text-[10px] uppercase tracking-widest font-semibold" data-testid="editor-saved-state">
              ✓ saved
            </span>
          )}
          {ccOn && (
            <span className="text-[10px]" data-testid="claim-flagged-count">
              {checking ? 'checking claims…' : `${claims.length} claims · ${flagged} flagged`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={ccOn ? 'default' : 'outline'} onClick={toggleCc}
            data-testid="claim-confidence-toggle">
            <ShieldCheck className="w-3 h-3 mr-1" /> Claim check {ccOn ? 'on' : 'off'}
          </Button>
          {ccOn && (
            <Button size="sm" variant="outline" onClick={() => runCheck(value, 0, 0, value.length, false)}
              disabled={checking} data-testid="claim-check-section-btn">
              {checking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Check section
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleSuggest} disabled={suggesting}
            className="border-[#1d4ed8]/40 text-[#1d4ed8] hover:bg-[#1d4ed8]/5 dark:text-[#7aa9ff]"
            data-testid="editor-ai-suggest-btn">
            {suggesting
              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Thinking…</>
              : <><Sparkles className="w-3 h-3 mr-1" /> AI suggest →</>}
          </Button>
          <Button size="sm" variant="ghost" onClick={handleManualSave} disabled={saving || !dirty} data-testid="editor-save-btn">
            <Save className="w-3 h-3 mr-1" /> Save
          </Button>
          <Button size="sm" variant="outline" onClick={onClose} data-testid="editor-close-btn">
            <Eye className="w-3 h-3 mr-1" /> View
          </Button>
        </div>
      </div>

      <div className="relative rounded-lg border border-border bg-background focus-within:ring-2 focus-within:ring-[#1d4ed8]">
        {ccOn && !!claims.length && (
          <ClaimHighlightOverlay text={value} marks={claims} innerRef={overlayRef} className={boxCls} />
        )}
        <textarea
          ref={taRef}
          value={value}
          onChange={handleChange}
          onSelect={handleSelect}
          onClick={handleSelect}
          onKeyUp={handleSelect}
          onScroll={syncScroll}
          rows={26}
          placeholder="Write your section here. Place your cursor where you want the AI to continue, then click 'AI suggest →' for a 1-2 sentence evidence-grounded continuation that completes the current paragraph."
          spellCheck="true"
          data-testid="chapter-textarea"
          className={`${boxCls} relative bg-transparent text-foreground resize-y outline-none border-0 whitespace-pre-wrap break-words`}
        />
      </div>

      {ccOn && activeClaim && (
        <ClaimConfidenceCard claim={activeClaim} onClose={() => setActiveClaim(null)}
          onInsertCitation={insertCitation}
          onDismiss={() => {
            setClaims((prev) => prev.filter((c) => c !== activeClaim));
            setActiveClaim(null);
          }} />
      )}

      <p className="text-[11px] text-muted-foreground text-center">
        {ccOn
          ? 'Wavy underlines: red = no support in your library, amber = weak or indirect, green = supported. Click a marked sentence for the evidence and the fix.'
          : 'Tip: place the cursor where you\u2019d like the AI to pick up — it will complete the current paragraph using only your project\u2019s evidence library.'}
      </p>
    </div>
  );
}
